import React, { useState, useEffect } from 'react';
import Navbar from './components/Navbar';
import Hero from './components/Hero';
import LookbookSection from './components/LookbookSection';
import BundlesSection from './components/BundlesSection';
import Editorial from './components/Editorial';
import Reviews from './components/Reviews';
import FAQSection from './components/FAQSection';
import Footer from './components/Footer';
import CartDrawer from './components/CartDrawer';
import SafeIcon from './common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { motion, AnimatePresence } from 'framer-motion';
import './index.css';

function App() {
  const [isCartOpen, setIsCartOpen] = useState(false);
  const [cart, setCart] = useState([]);
  const [toast, setToast] = useState(null);

  // Load cart from local storage on mount (simulating persistence)
  useEffect(() => {
    const savedCart = localStorage.getItem('cozynest_cart');
    if (savedCart) {
      try {
        setCart(JSON.parse(savedCart));
      } catch (e) {
        console.error("Failed to parse cart");
      }
    }
  }, []);

  // Save cart to local storage whenever it changes
  useEffect(() => {
    localStorage.setItem('cozynest_cart', JSON.stringify(cart));
  }, [cart]);

  const showToast = (message) => {
    setToast(message);
    setTimeout(() => setToast(null), 3000);
  };

  const addToCart = (product) => {
    setCart(prev => {
      const existing = prev.find(item => item.id === product.id);
      if (existing) {
        return prev.map(item => 
          item.id === product.id ? { ...item, quantity: item.quantity + 1 } : item
        );
      }
      return [...prev, { ...product, quantity: 1 }];
    });
    showToast(`Added ${product.name} to your cart`);
    setIsCartOpen(true);
  };

  const removeFromCart = (productId) => {
    setCart(prev => prev.filter(item => item.id !== productId));
  };

  const updateQuantity = (productId, delta) => {
    setCart(prev => prev.map(item => {
      if (item.id === productId) {
        const newQuantity = Math.max(1, item.quantity + delta);
        return { ...item, quantity: newQuantity };
      }
      return item;
    }));
  };

  const clearCart = () => {
    setCart([]);
  };

  const cartItemCount = cart.reduce((total, item) => total + item.quantity, 0);

  return (
    <div className="min-h-screen bg-[#F9F8F6] font-sans text-[#2C2C2C] selection:bg-[#8B7355] selection:text-white overflow-x-hidden">
      <Navbar toggleCart={() => setIsCartOpen(true)} cartCount={cartItemCount} />
      
      <CartDrawer 
        isOpen={isCartOpen} 
        onClose={() => setIsCartOpen(false)} 
        cart={cart}
        updateQuantity={updateQuantity}
        removeFromCart={removeFromCart}
        clearCart={clearCart}
      />
      
      {/* Toast Notification */}
      <AnimatePresence>
        {toast && (
          <motion.div
            initial={{ opacity: 0, y: 50, x: '-50%' }}
            animate={{ opacity: 1, y: 0, x: '-50%' }}
            exit={{ opacity: 0, y: 20, x: '-50%' }}
            className="fixed bottom-6 left-1/2 z-[100] bg-[#2C2C2C] text-white px-6 py-3 rounded-sm shadow-xl flex items-center gap-3 text-sm font-medium tracking-wide"
          >
            <SafeIcon icon={FiIcons.FiCheckCircle} className="w-4 h-4 text-[#8B7355]" />
            {toast}
          </motion.div>
        )}
      </AnimatePresence>

      <main id="main-content">
        <Hero />
        <LookbookSection addToCart={addToCart} />
        <BundlesSection addToCart={addToCart} />
        <Editorial />
        <Reviews />
        <FAQSection />
      </main>

      <Footer />
    </div>
  );
}

export default App;